/**
 * The library's wiring: two reads, the scope in the address, the search box and the filter panel's
 * four facets. It decides nothing.
 *
 * Every decision this screen makes lives in `model/` — the two projections, the filter, the header's
 * counts, and which list an address is asking for. What is here is the plumbing that cannot be
 * tested without a renderer: two queries, a piece of component state, and a navigation.
 *
 * ── BOTH reads run in both scopes, and that is what the switch is made of ────────────────────
 * The segmented control prints a count beside each of its two words, so the screen has to know both
 * numbers whichever list it is drawing. Neither read is a second request for it: the library is the
 * header's three figures in every scope, and the assignment list is already fetched for the
 * inspector's follower strip. Two observers on one cache entry, one request — see
 * `api/assignmentQueries`.
 *
 * **`status` is THIS scope's read**, not both. A library that fails while the trainer is looking at
 * their clients' plans turns the header's figures into em dashes and leaves the list alone, which is
 * what actually happened rather than a failure of the screen.
 *
 * ── The scope is read from the URL rather than held here ─────────────────────────────────────
 * `/plans?zakres=klienci`, owner decision of 3 September 2026. Reading it from the address and
 * writing it back with `navigate` means the browser's Back button walks the trainer between the two
 * lists, and a link to one of them is a link to it. A `useState` beside the address would be a
 * second answer to the same question, and the two would disagree the first time somebody typed a
 * URL.
 *
 * `search: planScopeSearch(scope)` and never `{ ...current, ... }`: the default scope has to
 * SERIALISE TO NOTHING, and spreading the current search would carry the key it is trying to drop.
 *
 * ── The SKELETON GATE lives here, so `status` already carries the minimum life ───────────
 * Moved out of the screen on 21 September 2026. `status` is the read's three states with the gate's
 * tail folded in: it stays `loading` until the bars have had their 300ms, not until the answer
 * lands. That is the whole of the fix, because `planListStatus` asks `read === 'loading'` FIRST —
 * so the list card, `inspectorAbsenceOf`, the pane's own shape and the toolbar's disabled state all
 * fork on the same moment without any of them knowing a gate exists.
 *
 * Forking on the read's own `pending` instead is the defect `useSkeletonGate`'s header describes:
 * for an answer landing between 150 and 450ms the bars flash away when it arrives and the content
 * already on screen blinks to transparent and fades back in 300ms later. Two entrances, one load.
 *
 * ── The FILTER is not in the address, and the client facet does not survive the switch ───────
 * Owner decision: the facets stay out of the URL. Typ, Tryb and Dni describe a plan and both scopes
 * list plans, so they survive a switch; Klient describes a person and is dropped on the way back to
 * the templates. That rule is `dropClientFacet`'s, in `model/`, so it can be tested — this only
 * calls it at the moment the scope changes.
 */

import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { type SkeletonGate, useSkeletonGate } from '~/shared/lib/useSkeletonGate';

import { clientPlansQueryOptions } from '../api/assignmentQueries';
import { planLibraryQueryOptions } from '~/entities/plan-draft';
import type { ClientPlanRow } from '../model/clientPlanRow';
import {
  NO_FILTER,
  clientFacetOptions,
  dropClientFacet,
  filterClientPlanRows,
  filterTemplateRows,
  type ClientFacetOption,
  type PlanLibraryFilter,
} from '../model/planLibraryFilter';
import { planListStatus, type PlanListStatus } from '../model/planListStatus';
import { toPlanLibraryRows, type PlanLibraryRow } from '../model/planLibraryRow';
import { summarisePlanLibrary, type PlanLibrarySummary } from '../model/planLibraryView';
import {
  planScopeOf,
  planScopeSearch,
  validatePlanLibrarySearch,
  type PlanScope,
} from '../model/planScope';

/**
 * The rows the list draws, and which kind they are.
 *
 * A discriminated value rather than two arrays and a flag: the screen switches on it ONCE, and the
 * row component it reaches is decided by the same branch that narrows the type. Two nullable arrays
 * beside a scope would let a caller draw the wrong list for the scope and still compile.
 */
export type PlanLibraryList =
  | { readonly scope: 'templates'; readonly rows: readonly PlanLibraryRow[] }
  | { readonly scope: 'clients'; readonly rows: readonly ClientPlanRow[] };

export interface PlanLibraryState {
  /**
   * Three states, never a branch on the data. This scope's read WITH the skeleton gate's tail: it
   * stays `loading` until the bars have had their minimum life. Everything that forks on the wait
   * forks on this — see the header.
   */
  readonly status: 'loading' | 'error' | 'ready';
  /**
   * The wait itself, whole. `status === 'loading'` IS `gate.waiting`; the gate travels beside it so
   * a caller that needs the bars, the hand-over class and the wait together passes one value
   * instead of reassembling a `SkeletonGate` out of three fields.
   */
  readonly gate: SkeletonGate;
  /** What the list draws: this scope's rows, narrowed by the search box and the facets. */
  readonly list: PlanLibraryList;
  /**
   * Which of the five absences the list is in, or that it has rows. One decision in `model/`, read
   * by the list card and by the inspector beside it — see `planListStatus`.
   */
  readonly listStatus: PlanListStatus;
  /**
   * This scope's rows BEFORE anything narrows them, which is what the prefetch queue seeds from.
   *
   * The queue used to seed from the narrowed list, so every keystroke tore it down and rebuilt it
   * with a fresh budget: eight characters over eight different subsets could start eight rounds of
   * the window's worth of full-plan reads, which is the unbounded fan-out the window exists to
   * stop. The filter reaches the queue through `promote` and nowhere else.
   */
  readonly scopeRows: PlanLibraryList;
  /** How many rows this scope has before anything narrows it — the fork between the two empties. */
  readonly totalCount: number;
  /** The switch's two figures. `null` where the read has not answered; never a zero it cannot vouch for. */
  readonly templateCount: number | null;
  readonly clientPlanCount: number | null;
  /** The header's three figures, over the WHOLE library, in both scopes. `null` until it lands. */
  readonly summary: PlanLibrarySummary | null;
  readonly scope: PlanScope;
  readonly setScope: (scope: PlanScope) => void;
  readonly filter: PlanLibraryFilter;
  readonly setFilter: (filter: PlanLibraryFilter) => void;
  /** Who the Klient tab offers: every client with a live plan, whatever the current selection is. */
  readonly clientOptions: readonly ClientFacetOption[];
  readonly retry: () => void;
}

export function usePlanLibrary(): PlanLibraryState {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // `strict: false` and re-validated through the slice's OWN validator rather than cast: one
  // definition of which parameters this screen understands, and the read is as safe as a strict one.
  const scope = planScopeOf(validatePlanLibrarySearch(useSearch({ strict: false })));

  const [filter, setFilter] = useState(NO_FILTER);
  const library = useQuery(planLibraryQueryOptions());
  // The reader's own locale, not the content language: this read carries no `lang` on the wire and
  // the parameter is only the collator that sorts client names.
  const clientPlans = useQuery(clientPlansQueryOptions(i18n.language, (key) => t(key)));

  const read = scope === 'clients' ? clientPlans : library;
  // The screen's one gate, over THIS scope's read. `waiting` rather than `read.isPending`: the wait
  // branch has to outlive the answer by the bars' minimum life, or the content lands at full
  // opacity and then fades in again 300ms later. See the header.
  const gate = useSkeletonGate(read.isPending);
  const status: PlanLibraryState['status'] = gate.waiting
    ? 'loading'
    : read.isError
      ? 'error'
      : 'ready';

  const templates = toPlanLibraryRows(library.data, (key) => t(key));
  const assigned = clientPlans.data ?? [];

  // The endpoint already orders the library by `UpdatedAt ?? CreatedAt` descending
  // (`PlanTemplateRepository.GetLibraryByOwnerAsync`) and owner decision D1 drops the sort control;
  // the clients' plans are ordered by `toClientPlanRows` on the client's surname. So both lists are
  // drawn in the order they arrived, narrowed and nothing else.
  const scopeRows: PlanLibraryList =
    scope === 'clients'
      ? { scope: 'clients', rows: assigned }
      : { scope: 'templates', rows: templates };
  const list: PlanLibraryList =
    scope === 'clients'
      ? { scope: 'clients', rows: status === 'ready' ? filterClientPlanRows(assigned, filter) : [] }
      : { scope: 'templates', rows: status === 'ready' ? filterTemplateRows(templates, filter) : [] };
  const totalCount = scopeRows.rows.length;

  return {
    status,
    gate,
    list,
    listStatus: planListStatus({ read: status, scope, totalCount, rowCount: list.rows.length }),
    scopeRows,
    totalCount,
    templateCount: library.isSuccess ? templates.length : null,
    clientPlanCount: clientPlans.isSuccess ? assigned.length : null,
    summary: library.isSuccess ? summarisePlanLibrary(templates) : null,
    scope,
    setScope: (next) => {
      // The facet goes at the moment the scope does, not on the next render: a panel opened on the
      // templates must never show a Klient count, and the list under it must never be narrowed by
      // one.
      if (next === 'templates') setFilter(dropClientFacet);
      void navigate({ to: '/plans', search: planScopeSearch(next) });
    },
    filter,
    setFilter,
    // From the UNFILTERED rows: a tab listing only the clients who survive the current selection
    // would remove the option the trainer just chose. See `clientFacetOptions`.
    clientOptions: clientFacetOptions(assigned),
    retry: () => {
      void read.refetch();
    },
  };
}
